import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import z from 'zod';
import {
  AlbumCard,
  AlbumEmpty,
  AlbumFormDialog,
  type AlbumFormValues,
  AlbumSummary,
  ConfirmDeleteDialog,
  DropOverlay,
  GalleryGrid,
  GalleryIntro,
  type GalleryPhoto,
  InstagramImportDialog,
  NewAlbumCard,
  PhotoLightbox,
  type PhotoSort,
  PhotoTile,
  PhotoToolbar,
  PrimaryPill,
  plural,
  SelectionBar,
  TopbarActions,
  TopbarMoreMenu,
  TopbarPill,
  UploadStrip,
  useFileDrop,
} from '@/components/ui/admin/gallery';
import { Content } from '@/components/ui/content';
import { useToast } from '@/hooks/use-toast';
import { meta } from '@/lib/meta';
import { cn } from '@/lib/utils';
import {
  flattenPhotos,
  galleryKeys,
  uploadAlbumPhotos,
  useAlbumPhotos,
  useDeleteAlbums,
  useDeletePhotos,
  useGalleryStorage,
  useGalleryAlbum,
  useGalleryAlbums,
  useSaveAlbum,
  useSetAlbumCover,
} from '@/services/gallery';
import {
  connectInstagram,
  useDisconnectInstagram,
  useImportInstagramHistory,
  useImportInstagramPost,
  useInstagramAccounts,
} from '@/services/instagram';

/**
 * Galeria (módulo 06, prints 14–17 do `aliste-se-admin-kit`). O álbum aberto fica na URL
 * (`?album=<id>`), não num segmento: `/gallery/<id>` viraria permissão de `update` no `AppLayout`.
 */
export const Route = createFileRoute('/_app/gallery')({
  head: () => meta({ label: 'Galeria' }),
  validateSearch: z.object({
    album: z.string().optional(),
    /** Abre o "Importar do Instagram" do álbum (volta do login do Instagram). */
    importar: z.coerce.boolean().optional(),
    /** Volta do login do Instagram (`GET /instagram/callback`): connected | cancelled | error. */
    instagram: z.string().optional(),
    username: z.string().optional(),
    message: z.string().optional(),
  }),
  component: RouteComponent,
});

/** "Até 100 por envio" (faixa de envio do kit). */
const MAX_FILES_PER_UPLOAD = 100;

/** Álbum que pediu a conexão com o Instagram — a volta do login reabre ele com o dialog aberto. */
const INSTAGRAM_RETURN_KEY = 'gallery:instagram-album';

function RouteComponent() {
  const { album, instagram, username, message } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const toast = useToast();

  const open = (id?: string) => navigate({ search: id ? { album: id } : {} });

  // Volta do login do Instagram: avisa e reabre o álbum de onde saiu.
  const handledRef = useRef(false);
  useEffect(() => {
    if (!instagram || handledRef.current) return;
    handledRef.current = true;

    if (instagram === 'connected') toast.success(`Conta @${username ?? ''} conectada`);
    else if (instagram === 'error')
      toast.error(message || 'Não foi possível conectar a conta do Instagram.');

    const back = sessionStorage.getItem(INSTAGRAM_RETURN_KEY);
    sessionStorage.removeItem(INSTAGRAM_RETURN_KEY);
    void navigate({ search: back ? { album: back, importar: true } : {}, replace: true });
  }, [instagram, username, message, navigate, toast]);

  return album ? (
    <AlbumStep key={album} albumId={album} onBack={() => open()} />
  ) : (
    <AlbumsStep onOpen={open} />
  );
}

/** Esc sai do modo seleção quando nada estiver aberto por cima. */
function useEscToExit(active: boolean, onExit: () => void) {
  useEffect(() => {
    if (!active) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExit();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [active, onExit]);
}

/* ─────────────── Álbuns ─────────────── */

type FormState = (AlbumFormValues & { id?: string }) | null;

function AlbumsStep({ onOpen }: { onOpen: (id: string) => void }) {
  const toast = useToast();
  const { data: albums = [], isLoading } = useGalleryAlbums();
  const { mutateAsync: saveAlbum, isPending: saving } = useSaveAlbum();
  const { mutateAsync: deleteAlbums, isPending: deleting } = useDeleteAlbums();

  const [form, setForm] = useState<FormState>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [toDelete, setToDelete] = useState<string[] | null>(null);

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelected([]);
  }, []);
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  useEscToExit(selectMode && !form && !toDelete, exitSelect);

  const save = async (values: AlbumFormValues) => {
    try {
      const saved = await saveAlbum({ ...values, id: form?.id });
      setForm(null);
      // Criar já abre o álbum, pra arrastar as fotos direto.
      if (!form?.id) onOpen(saved.id);
    } catch {
      // toast no `useSaveAlbum`
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteAlbums(toDelete);
      toast.success(
        `${plural(toDelete.length, 'álbum excluído', 'álbuns excluídos')} com sucesso`,
      );
      exitSelect();
    } catch {
      // toast no `useDeleteAlbums`
    } finally {
      setToDelete(null);
    }
  };

  const photos = albums.reduce((n, a) => n + (a.photosCount ?? 0), 0);
  const { data: storage } = useGalleryStorage();
  const n = toDelete?.length ?? 0;
  const newAlbum = () => setForm({ title: '', description: '', isPublic: false });

  return (
    <Content
      isLoading={isLoading}
      breadcrumbs={{
        title: 'Galeria',
        items: [{ label: 'Conteúdo' }, { label: 'Galeria', active: true }],
        action: (
          <TopbarActions>
            <TopbarPill
              icon={selectMode ? 'x' : 'square-check'}
              active={selectMode}
              onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
            >
              {selectMode ? 'Cancelar seleção' : 'Selecionar'}
            </TopbarPill>
            <PrimaryPill onClick={newAlbum}>Novo álbum</PrimaryPill>
          </TopbarActions>
        ),
      }}
    >
      <div className="flex w-full flex-col gap-6">
        <GalleryIntro albums={albums.length} photos={photos} storage={storage} />
        <GalleryGrid>
          {albums.map((a) => (
            <AlbumCard
              key={a.id}
              album={a}
              selectMode={selectMode}
              selected={selected.includes(a.id)}
              onOpen={() => (selectMode ? toggle(a.id) : onOpen(a.id))}
              onEdit={() =>
                setForm({
                  id: a.id,
                  title: a.title,
                  description: a.description ?? '',
                  isPublic: !!a.isPublic,
                })
              }
              onDelete={() => setToDelete([a.id])}
            />
          ))}
          {!selectMode && <NewAlbumCard onClick={newAlbum} />}
        </GalleryGrid>
      </div>

      {selectMode && selected.length > 0 && (
        <SelectionBar
          label={plural(selected.length, 'selecionado', 'selecionados')}
          onSelectAll={
            selected.length < albums.length
              ? () => setSelected(albums.map((a) => a.id))
              : undefined
          }
          onDelete={() => setToDelete(selected)}
          onCancel={exitSelect}
        />
      )}

      <AlbumFormDialog
        open={!!form}
        values={form}
        loading={saving}
        onClose={() => setForm(null)}
        onSubmit={save}
      />
      <ConfirmDeleteDialog
        open={!!toDelete}
        loading={deleting}
        title={`Excluir ${plural(n, 'álbum', 'álbuns')}?`}
        body={`Todas as fotos dentro ${n === 1 ? 'dele' : 'deles'} também serão removidas da galeria pública. Não pode ser desfeito.`}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </Content>
  );
}

/* ─────────────── Álbum aberto ─────────────── */

type Tile = GalleryPhoto & { uploading?: boolean };

function AlbumStep({ albumId, onBack }: { albumId: string; onBack: () => void }) {
  const toast = useToast();
  const { importar } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  const [sort, setSort] = useState<PhotoSort>('recent');
  const {
    data: album,
    isLoading: loadingAlbum,
    isError: albumMissing,
  } = useGalleryAlbum(albumId);
  const {
    data,
    isLoading: loadingPhotos,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useAlbumPhotos(albumId, sort === 'recent' ? 'desc' : 'asc');
  const { mutateAsync: saveAlbum, isPending: saving } = useSaveAlbum();
  const { mutateAsync: setCoverMutation } = useSetAlbumCover();
  const { mutateAsync: deletePhotos, isPending: deleting } = useDeletePhotos();

  const [temp, setTemp] = useState<Tile[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [lb, setLb] = useState<number | null>(null);
  const [form, setForm] = useState<(AlbumFormValues & { id: string }) | null>(null);
  const [toDelete, setToDelete] = useState<string[] | null>(null);
  const [igOpen, setIgOpen] = useState(!!importar);
  const [igError, setIgError] = useState<string | null>(null);
  const { data: ig } = useInstagramAccounts(igOpen);
  const { mutateAsync: importPost, isPending: importing } = useImportInstagramPost();
  const { mutate: disconnectInstagram } = useDisconnectInstagram();
  const { mutate: importInstagramHistory, isPending: startingHistory } =
    useImportInstagramHistory();

  // `?importar` só serve pra abrir o dialog na volta do login: tira da URL.
  useEffect(() => {
    if (importar) void navigate({ search: { album: albumId }, replace: true });
  }, [importar, albumId, navigate]);

  const real = flattenPhotos(data);
  const photos: Tile[] = [...temp, ...real];
  const total = data?.pages[0]?.pagination.total ?? 0;

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelected([]);
  }, []);

  useEscToExit(selectMode && !toDelete && lb == null && !form && !igOpen, exitSelect);

  // Link antigo ou álbum excluído (`?album=` inválido): volta pra lista.
  // `toast`/`onBack` mudam a cada render: a ref garante um aviso só.
  const leftRef = useRef(false);
  useEffect(() => {
    if (!albumMissing || leftRef.current) return;
    leftRef.current = true;
    toast.error('Álbum não encontrado');
    onBack();
  }, [albumMissing, onBack, toast]);

  // Carrega a próxima página quando o fim da grade entra na tela.
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !hasNextPage) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /* Envio: mostra as fotos na hora (object URL + barra), troca pelas reais quando a API responde. */
  const upload = async (picked: File[]) => {
    if (!picked.length) return;
    if (picked.length > MAX_FILES_PER_UPLOAD) {
      toast.warning(
        `Enviando as primeiras ${MAX_FILES_PER_UPLOAD} fotos (limite por envio).`,
      );
    }
    const files = picked.slice(0, MAX_FILES_PER_UPLOAD);
    const stamp = Date.now();
    const tiles: Tile[] = files.map((f, i) => ({
      id: `upload-${stamp}-${i}`,
      url: URL.createObjectURL(f),
      createdAt: new Date(),
      uploading: true,
    }));

    setSort('recent');
    setTemp((t) => [...tiles, ...t]);

    try {
      const created = await uploadAlbumPhotos(albumId, files);
      toast.success(
        `${plural(created.length, 'foto enviada', 'fotos enviadas')} com sucesso`,
      );
    } catch (e: any) {
      toast.error(e?.message ?? 'Erro ao enviar fotos. Tente novamente.');
    } finally {
      // Espera a grade recarregar antes de tirar as provisórias, pra não piscar vazio.
      await queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      setTemp((t) => t.filter((x) => !tiles.includes(x)));
      for (const t of tiles) URL.revokeObjectURL(t.url);
    }
  };

  const dragging = useFileDrop(!form && !toDelete && lb == null && !igOpen, upload);

  const openInstagram = () => {
    setIgError(null);
    setIgOpen(true);
  };

  const importInstagram = async (url: string) => {
    setIgError(null);
    try {
      const res = await importPost({ albumId, url });
      setSort('recent');
      setIgOpen(false);
      if (res.imported > 0) {
        toast.success(
          `${plural(res.imported, 'foto importada', 'fotos importadas')} de @${res.account}`,
        );
      } else {
        toast.warning(
          res.alreadyImported > 0
            ? 'Esse post já foi importado neste álbum.'
            : 'O post não tem fotos disponíveis para importar.',
        );
      }
    } catch (e: any) {
      setIgError(e?.message ?? 'Não foi possível importar o post.');
    }
  };

  const startInstagramConnect = async () => {
    sessionStorage.setItem(INSTAGRAM_RETURN_KEY, albumId);
    try {
      await connectInstagram();
    } catch (e: any) {
      sessionStorage.removeItem(INSTAGRAM_RETURN_KEY);
      setIgError(e?.message ?? 'Não foi possível abrir o login do Instagram.');
    }
  };

  const setCover = async (url: string) => {
    try {
      await setCoverMutation({ id: albumId, url });
      toast.success('Capa do álbum atualizada');
    } catch {
      // toast no `useSetAlbumCover`
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deletePhotos(toDelete);
      toast.success(res.message);
      const left = real.filter((p) => !toDelete.includes(p.id));
      if (lb != null) setLb(left.length ? Math.min(lb, left.length - 1) : null);
      exitSelect();
    } catch {
      // toast no `useDeletePhotos`
    } finally {
      setToDelete(null);
    }
  };

  const save = async (values: AlbumFormValues) => {
    try {
      await saveAlbum({ ...values, id: albumId });
      setForm(null);
    } catch {
      // toast no `useSaveAlbum`
    }
  };

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pick = () => fileRef.current?.click();
  const toggleSelectMode = () => {
    setSelectMode((s) => !s);
    setSelected([]);
  };
  const editAlbum = () =>
    album &&
    setForm({
      id: album.id,
      title: album.title,
      description: album.description ?? '',
      isPublic: !!album.isPublic,
    });
  const n = toDelete?.length ?? 0;
  const title = album?.title ?? '';

  return (
    <Content
      isLoading={loadingAlbum}
      breadcrumbs={{
        title: title || 'Álbum',
        items: [
          { label: 'Conteúdo' },
          { label: 'Galeria', onClick: onBack },
          { label: 'Álbum', active: true },
        ],
        onBack,
        action: (
          <TopbarActions>
            {/* Celular: "Selecionar"/"Editar" vão pro menu "⋯"; em seleção fica só o "×" (branco). */}
            <span className={cn('contents', !selectMode && 'max-sm:hidden')}>
              <TopbarPill
                icon={selectMode ? 'x' : 'square-check'}
                active={selectMode}
                onClick={toggleSelectMode}
              >
                {selectMode ? 'Cancelar seleção' : 'Selecionar'}
              </TopbarPill>
            </span>
            <span className="contents max-sm:hidden">
              <TopbarPill icon="pencil" onClick={editAlbum}>
                Editar
              </TopbarPill>
            </span>
            {!selectMode && (
              <span className="contents max-sm:hidden">
                <TopbarPill icon="instagram" onClick={openInstagram}>
                  Instagram
                </TopbarPill>
              </span>
            )}
            {!selectMode && (
              <TopbarMoreMenu
                className="sm:hidden"
                items={[
                  {
                    icon: 'square-check',
                    label: 'Selecionar',
                    onSelect: toggleSelectMode,
                  },
                  { icon: 'pencil', label: 'Editar álbum', onSelect: editAlbum },
                  {
                    icon: 'instagram',
                    label: 'Importar do Instagram',
                    onSelect: openInstagram,
                  },
                ]}
              />
            )}
            <PrimaryPill onClick={pick}>Adicionar fotos</PrimaryPill>
          </TopbarActions>
        ),
      }}
    >
      <input
        ref={fileRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          void upload(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />

      <div className="flex w-full flex-col gap-5">
        {album && <AlbumSummary album={album} count={total} />}
        <UploadStrip onPick={pick} uploading={temp.length} />
        <PhotoToolbar sort={sort} onSort={setSort} selectMode={selectMode} />

        {!loadingPhotos && photos.length === 0 ? (
          <AlbumEmpty onPick={pick} />
        ) : (
          <GalleryGrid min={210} gap="sm">
            {photos.map((p) => (
              <PhotoTile
                key={p.id}
                photo={p}
                uploading={p.uploading}
                isCover={!!album?.coverImage && p.url === album.coverImage}
                selectMode={selectMode}
                selected={selected.includes(p.id)}
                onClick={() =>
                  selectMode ? toggle(p.id) : setLb(real.findIndex((x) => x.id === p.id))
                }
                onMakeCover={() => setCover(p.url)}
                onDelete={() => setToDelete([p.id])}
              />
            ))}
          </GalleryGrid>
        )}

        <div ref={moreRef}>
          {hasNextPage && (
            <Loader2 className="mx-auto my-4 size-6 animate-spin text-faint" />
          )}
        </div>
      </div>

      {selectMode && selected.length > 0 && (
        <SelectionBar
          label={plural(selected.length, 'selecionada', 'selecionadas')}
          onSelectAll={
            selected.length < real.length
              ? () => setSelected(real.map((p) => p.id))
              : undefined
          }
          onCover={
            selected.length === 1
              ? () => {
                  const p = real.find((x) => x.id === selected[0]);
                  if (p) void setCover(p.url);
                  exitSelect();
                }
              : undefined
          }
          onDelete={() => setToDelete(selected)}
          onCancel={exitSelect}
        />
      )}

      <DropOverlay show={dragging} albumTitle={title} />

      <PhotoLightbox
        photos={real}
        index={lb}
        albumTitle={title}
        coverUrl={album?.coverImage}
        paused={!!toDelete}
        onIndex={setLb}
        onClose={() => setLb(null)}
        onCover={(p) => setCover(p.url)}
        onDelete={(p) => setToDelete([p.id])}
      />

      <AlbumFormDialog
        open={!!form}
        values={form}
        loading={saving}
        onClose={() => setForm(null)}
        onSubmit={save}
      />
      <InstagramImportDialog
        open={igOpen}
        albumTitle={title}
        configured={ig?.configured ?? true}
        accounts={ig?.accounts ?? []}
        loading={importing}
        error={igError}
        onClose={() => setIgOpen(false)}
        onImport={importInstagram}
        onConnect={startInstagramConnect}
        onDisconnect={(a) => disconnectInstagram(a.id)}
        importingHistory={startingHistory || ig?.importingHistory}
        onImportHistory={() => importInstagramHistory()}
      />
      <ConfirmDeleteDialog
        open={!!toDelete}
        loading={deleting}
        title={`Excluir ${plural(n, 'foto', 'fotos')}?`}
        body="As fotos saem do álbum e da galeria pública. Não pode ser desfeito."
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </Content>
  );
}
